import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import AccountChangePassword from "@/components/AccountChangePassword";
import DeleteAccountButton from "@/components/DeleteAccountButton";
import VisionAiWizard from "@/components/VisionAiWizard";
import { RO_COUNTY_BY_CODE } from "@/lib/romaniaCounties";
import {
  User as UserIcon,
  Mail,
  Phone,
  Building2,
  MapPin,
  Globe,
  CalendarDays,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0"
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 text-sm text-zinc-400 shrink-0">
        <Icon size={15} strokeWidth={1.5} className="text-zinc-500" />
        {label}
      </div>
      <div className="text-sm text-white text-right truncate">{value}</div>
    </div>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [visionWizardOpen, setVisionWizardOpen] = useState(false);
  if (!user) return null;

  const notSet = (
    <span className="text-zinc-500 italic">{t("account.not_set")}</span>
  );
  const countyName = user.county_code
    ? RO_COUNTY_BY_CODE[user.county_code] || user.county_code
    : null;
  const countryName =
    !user.country_code || user.country_code === "RO"
      ? "România"
      : user.country_name || user.country_code;
  const roleLabel = t(`account.role_${user.role}`, { defaultValue: user.role });
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString(
        i18n.language === "ro" ? "ro-RO" : "en-GB",
        { day: "numeric", month: "long", year: "numeric" }
      )
    : null;

  return (
    <div className="min-h-screen bg-background grain" data-testid="account-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
            {t("account.title")}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">{t("account.subtitle")}</p>
        </div>

        <section
          className="rounded-xl border border-white/10 bg-zinc-950/60 p-5 sm:p-6"
          data-testid="account-profile-card"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-orange-400 font-semibold mb-3">
            {t("account.section_profile")}
          </h2>
          <Row icon={UserIcon} label={t("account.name")} value={user.name || notSet} testId="account-row-name" />
          <Row icon={Mail} label={t("account.email")} value={user.email} testId="account-row-email" />
          <Row icon={Phone} label={t("account.phone")} value={user.phone || notSet} testId="account-row-phone" />
          {user.client_type === "company" && (
            <>
              <Row icon={Building2} label={t("account.company")} value={user.company || notSet} testId="account-row-company" />
              <Row icon={Building2} label={t("account.vat_id")} value={user.vat_id || notSet} testId="account-row-vat" />
            </>
          )}
          <Row
            icon={UserIcon}
            label={t("account.client_type")}
            value={
              user.client_type === "company"
                ? t("account.client_company")
                : t("account.client_individual")
            }
            testId="account-row-client-type"
          />
          <Row icon={ShieldCheck} label={t("account.role")} value={roleLabel} testId="account-row-role" />
          <Row icon={Globe} label={t("account.country")} value={countryName} testId="account-row-country" />
          <Row icon={MapPin} label={t("account.county")} value={countyName || notSet} testId="account-row-county" />
          {memberSince && (
            <Row icon={CalendarDays} label={t("account.member_since")} value={memberSince} testId="account-row-member-since" />
          )}
        </section>

        <section
          className="mt-6 rounded-xl border border-white/10 bg-zinc-950/60 p-5 sm:p-6"
          data-testid="account-security-card"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-orange-400 font-semibold mb-2">
            {t("account.section_security")}
          </h2>
          <p className="text-sm text-zinc-400 mb-4">{t("account.security_hint")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <AccountChangePassword />
            <DeleteAccountButton />
          </div>
        </section>

        {/* Sprint 1 Vision AI — profile editor (partner/sales/admin only) */}
        {["partner", "sales", "admin"].includes(user.role) && (
          <section
            className="mt-6 rounded-xl border border-orange-500/20 bg-orange-500/5 p-5 sm:p-6"
            data-testid="account-vision-ai-card"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 font-semibold mb-2 flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.5} />
              {t("vision_ai.banner_eyebrow")}
            </div>
            <h2 className="font-outfit font-bold text-white text-lg tracking-tight">
              {t("vision_ai.wizard_title")}
            </h2>
            <p className="text-sm text-zinc-400 mt-1 mb-4 max-w-2xl">
              {t("vision_ai.wizard_subtitle")}
            </p>
            <button
              type="button"
              onClick={() => setVisionWizardOpen(true)}
              data-testid="account-vision-edit-btn"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white transition"
            >
              <Sparkles size={14} strokeWidth={1.5} />
              {t("vision_ai.banner_cta_edit")}
            </button>
            <VisionAiWizard
              open={visionWizardOpen}
              onOpenChange={setVisionWizardOpen}
            />
          </section>
        )}
      </main>
    </div>
  );
}
